import * as React from 'react'
import * as Redux from 'react-redux'
import styled from 'styled-components'

import { t } from '@owl-nest/localize'
import { BREAKPOINTS, ChannelCard, glyphs, Link, styles as S, ClickableTag } from '@ulule/owl-kit-components/next'

import * as model from '@owl-nest/models'
import { Status } from '@owl-nest/redux-wrapper'
import { useAuth } from '@owl-nest/redux-wrapper/hooks'
import { useLazyInitialLoad } from '@owl-nest/hooks'
import * as env from '@owl-nest/config'

import { RootState, AppState } from '../../../../../reducers'
import { loadProjectChannels } from '../../../../../actions/channel'
import * as projectModel from '../../../../models/project'

type ExplorerProps = unknown

export function Explorer(): React.ReactElement<ExplorerProps> {
  const { project: projectData, channel } = Redux.useSelector<RootState, AppState>((state) => state.application)
  const dispatch = Redux.useDispatch()
  const project = projectData.data
  const mainTag = model.project.mainTag<projectModel.ExtraFields>(project)

  const auth = useAuth()
  const isLoggedIn = auth.type === 'loggedin'
  const userConfig = env.useUserEnv()

  const channelsWrapperRef = React.useRef<HTMLUListElement>(null)
  useLazyInitialLoad({
    target: channelsWrapperRef,
    load: () => {
      if (channel.status.load === Status.PRISTINE) {
        dispatch(loadProjectChannels(project.id, { pagination: `?limit=5&lang=${userConfig.lang}`, forceClean: true }))
      }
    },
  })

  return (
    <ExplorerWrapper $addPaddingBottom={isLoggedIn}>
      <Header>
        <S.heading.M>{t('Discover similar projects')}</S.heading.M>
        {isLoggedIn && (
          <ReportLink>
            <Link kind="secondary" withIcon href="mailto:support@ulule.com">
              <glyphs.stroke.Alert size={14} />
              {t('Flag this project')}
            </Link>
          </ReportLink>
        )}
      </Header>
      <TagWrapper>
        {mainTag && project.main_tag?.absolute_url && (
          <Tag
            key="mainTag"
            icon={<glyphs.stroke.Tag size={14} />}
            href={project.main_tag?.absolute_url}
            forwardedAs="a"
            label={<S.heading.XXXS>{mainTag}</S.heading.XXXS>}
          />
        )}
        {project.tags?.map((tag, index) => (
          <Tag
            key={index}
            icon={<glyphs.stroke.Hash size={14} />}
            forwardedAs="a"
            href={tag.absolute_url}
            label={<S.heading.XXXS>{model.project.tagName<projectModel.ExtraFields>(tag, project)}</S.heading.XXXS>}
          />
        ))}
      </TagWrapper>
      <ChannelList ref={channelsWrapperRef}>
        {channel.channels?.map((item, index) => {
          const isPartner = item.partner?.id
          const user = item.user
          const description = isPartner
            ? t('With the support of %(name)s', { name: user.name })
            : t('Curated by %(name)s', { name: user.name })
          return (
            <ChannelItem key={index}>
              <ChannelCard
                avatar={model.user.avatar(user, '40x40')}
                description={description}
                href={item.absolute_url}
                src={model.channel.ctaBackgroundImage(item)}
                title={model.channel.name(item)}
              />
            </ChannelItem>
          )
        })}
      </ChannelList>
    </ExplorerWrapper>
  )
}

const ExplorerWrapper = styled.div<{ $addPaddingBottom: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 20px 0;
  position: relative;

  ${({ $addPaddingBottom }) => $addPaddingBottom && `padding-bottom: 60px;`}

  @media screen and ${BREAKPOINTS.TABLET} {
    padding: 20px;
  }
`

const Header = styled.div`
  display: inline-flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 0 20px 20px 20px;
`

const ReportLink = styled(S.copy.S.regular)`
  position: absolute;
  bottom: 30px;

  @media screen and ${BREAKPOINTS.TABLET} {
    position: static;
  }
`

const TagWrapper = styled.div`
  display: flex;
  overflow: scroll;
  padding-left: 20px;

  @media screen and ${BREAKPOINTS.TABLET} {
    overflow: auto;
  }
`

const Tag = styled(ClickableTag)`
  margin-right: 14px;
  margin-bottom: 10px;
  flex-shrink: 0;
  text-decoration: none;

  &:last-child {
    margin-right: 0;
  }

  ${glyphs.Icon} {
    margin-top: 0px;
  }
`

export const ChannelList = styled.ul`
  display: flex;
  margin: 0;
  padding: 0;
  list-style: none;
  width: 100%;
  flex-wrap: nowrap;
  justify-content: flex-start;
  overflow: scroll;

  @media screen and ${BREAKPOINTS.TABLET} {
    flex-wrap: wrap;
    overflow: auto;
    margin-bottom: 40px;
  }
`

export const ChannelItem = styled.li`
  margin: 15px 0px 20px 20px;

  &:last-child {
    padding-right: 20px;
  }
`
